import { apiClient } from "../client";
import type { LaravelPaginator } from "./catalog";

/**
 * The blog, read publicly and written from two sides.
 *
 * `blog.posts/post/related/categories` are the public reads — no account, only
 * published articles.
 *
 * `blog.mine.*` is the **astrologer's own** authoring surface. An astrologer
 * writes an article and submits it; an admin has to approve it before it
 * appears on the public blog. Nothing in `mine` can publish — the server
 * accepts no `status` there at all — so a client cannot get an article onto the
 * site by sending the right field.
 *
 *   draft ──submit──▶ pending_review ──(admin approves)──▶ published
 *     ▲                     │
 *     └──── edit ───────────┴──(admin rejects)──▶ rejected
 *
 * **Content is plain text, not HTML.** `content` on the author endpoints is
 * what the astrologer typed; the server renders it to the small HTML subset the
 * public blog is allowed to show and keeps the original in `content_source` (so
 * the composer can reopen it). Sending HTML gets it escaped and shown as text —
 * the public page injects `content` with innerHTML, so author-supplied markup is
 * never accepted. `BLOG_MARKUP_HELP` below is that subset, for the composer's
 * cheatsheet.
 */

export interface BlogCategory {
  id: number;
  name: string;
  slug: string;
  description?: string;
  posts_count?: number;
}

export interface BlogAuthor {
  id: number;
  first_name: string;
  middle_name?: string | null;
  last_name?: string | null;
  /** The name they publish under (astrologer display name, else their full name). */
  display_name?: string | null;
  /** True when the author has an astrologer profile — i.e. show a real byline. */
  is_astrologer?: boolean;
  /** Their public profile slug, for linking the byline. */
  astrologer_slug?: string | null;
  /** One-line credential under the byline (the profile's short intro). */
  headline?: string | null;
  photo_url?: string | null;
  experience?: number | null;
}

/** Where a post sits in the editorial pipeline. */
export type BlogPostStatus = "draft" | "pending_review" | "published" | "rejected";

/** Blog post as returned by the live /blog-posts endpoints. */
export interface BlogPost {
  id: number;
  title: string;
  slug: string;
  /** Often empty upstream — fall back to `content`. */
  excerpt: string | null;
  /** Full HTML body. */
  content: string;
  blog_category_id: number;
  author_id: number;
  status: BlogPostStatus | string;
  published_at: string | null;
  category: BlogCategory | null;
  author: BlogAuthor | null;
  featured_image_url: string | null;
  /** Public reads of the article — what `sort: "popular"` orders by. */
  views?: number;
}

/** An astrologer's own post: everything above, plus the review trail. */
export interface MyBlogPost extends BlogPost {
  /** The text the author typed. This is what the composer reopens, not `content`. */
  content_source: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  /** Shown to the author verbatim when the admin sent it back. */
  rejection_reason: string | null;
  reviewed_by: { id: number; name: string } | null;
  /** The server's own verdict on what this post allows — don't re-derive it. */
  can_edit: boolean;
  can_submit: boolean;
}

export interface MyBlogPostsResponse {
  success?: boolean;
  message?: string;
  data: MyBlogPost[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    /** Tally across ALL of the author's posts, not just this page. */
    counts: Record<BlogPostStatus, number>;
  };
}

export interface BlogPostEnvelope {
  success?: boolean;
  message?: string;
  data: MyBlogPost;
}

export interface MyBlogPostPayload {
  title: string;
  /** Plain text in the markup subset — see BLOG_MARKUP_HELP. */
  content?: string;
  /** Left empty, the server writes the first ~160 characters of the article. */
  excerpt?: string;
  blog_category_id?: number | string;
  featured_image_url?: string;
  /** true = save AND hand it to the admin's review queue in one request. */
  submit?: boolean;
}

export interface BlogImageUploadResponse {
  success?: boolean;
  message?: string;
  data: { url: string };
}

export interface BlogPostsParams {
  page?: number;
  per_page?: number;
  search?: string;
  /** Category slug. */
  category?: string;
  category_id?: number;
  /** One author's articles only. */
  author_id?: number;
  sort?: "newest" | "oldest" | "popular" | "title";
}

/**
 * The formatting an astrologer may use, in the exact words the composer shows
 * them. Kept here next to the endpoints so the help text and the contract
 * cannot drift apart — the server's renderer (utils/blogMarkup.ts) is the
 * authority for what each line does.
 */
export const BLOG_MARKUP_HELP: { syntax: string; means: string }[] = [
  { syntax: "## Heading", means: "Section heading" },
  { syntax: "### Sub-heading", means: "Smaller heading" },
  { syntax: "**bold**", means: "Bold text" },
  { syntax: "*italic*", means: "Italic text" },
  { syntax: "- item", means: "Bulleted list" },
  { syntax: "1. item", means: "Numbered list" },
  { syntax: "> quote", means: "Quotation" },
  { syntax: "[text](https://…)", means: "Link" },
  { syntax: "blank line", means: "New paragraph" },
];

export const blog = {
  /**
   * NOTE: this endpoint returns the raw Laravel paginator — no envelope.
   * Search, category, sort and per_page are all applied server-side; do not
   * walk every page to filter in the browser.
   */
  posts: (params: BlogPostsParams = {}) =>
    apiClient.get<LaravelPaginator<BlogPost>>("/blog-posts", { params: { ...params } }),

  /** Single post by slug or id. Published only; counts as a read. */
  post: (identifier: string | number) =>
    apiClient.get<BlogPost>(`/blog-posts/${identifier}`),

  related: (identifier: string | number) =>
    apiClient.get<BlogPost[]>(`/blog-posts/${identifier}/related`),

  categories: () => apiClient.get<BlogCategory[]>("/blog-categories"),

  postsByCategory: (categorySlug: string) =>
    apiClient.get<LaravelPaginator<BlogPost>>(`/blog-categories/${categorySlug}/posts`),

  /**
   * The signed-in astrologer's own articles. 404 means "this account has no
   * astrologer profile"; 403 means the profile is not approved or not active.
   */
  mine: {
    list: (params: { page?: number; per_page?: number; status?: BlogPostStatus } = {}) =>
      apiClient.get<MyBlogPostsResponse>("/my-blog-posts", { params: { ...params } }),

    get: (id: number | string) => apiClient.get<BlogPostEnvelope>(`/my-blog-posts/${id}`),

    create: (payload: MyBlogPostPayload) =>
      apiClient.post<BlogPostEnvelope>("/my-blog-posts", payload),

    /** Only a draft or a rejected post can be edited — anything else is 409. */
    update: (id: number | string, payload: Partial<MyBlogPostPayload>) =>
      apiClient.put<BlogPostEnvelope>(`/my-blog-posts/${id}`, payload),

    /** Hand an already-saved draft to the admin's review queue. */
    submit: (id: number | string) =>
      apiClient.post<BlogPostEnvelope>(`/my-blog-posts/${id}/submit`),

    /** A draft or rejected post only; a submitted or published one is 409. */
    remove: (id: number | string) => apiClient.delete<{ success: boolean; message: string }>(`/my-blog-posts/${id}`),

    /**
     * Cover image, straight from the device. Field name `files`, images only,
     * 10 MB — the Content-Type override is what stops axios JSON-encoding the
     * FormData.
     */
    uploadImage: (file: File) => {
      const form = new FormData();
      form.append("files", file);
      return apiClient.post<BlogImageUploadResponse>("/my-blog-posts/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
  },
};
